import { usableRevenueCatKey } from './revenueCatKey'

/**
 * What billing needs from RevenueCat — a port, so the provider is tested
 * without the SDK and the SDK stays out of the main bundle.
 */
export type BillingPort = {
  /** Make `appUserId` the SDK identity; resolves to the ACTIVE entitlement ids. */
  identify(appUserId: string): Promise<readonly string[]>
  /** Forget the identity (sign-out). */
  reset(): Promise<void>
}

/**
 * The RevenueCat-backed port, or null when there is no usable key (missing,
 * dummy, malformed) — null means billing is unavailable and gates stay locked.
 *
 * The SDK is `import()`ed on first `identify`, so a signed-out visitor, or any
 * build without a usable key, never downloads it. Response shapes come from
 * the SDK's types; no live customer info has been observed.
 */
export function createRevenueCatPort(rawKey: string | undefined): BillingPort | null {
  const apiKey = usableRevenueCatKey(rawKey)
  if (apiKey === null) return null

  // Reset must not be the thing that downloads the SDK: if identify never ran
  // there is no identity to forget.
  let sdkUsed = false

  // Operations run one at a time, in call order. Without this a `reset()` could
  // run while `identify()` was still awaiting the SDK import: close() would
  // find nothing configured, then identify would configure the previous user
  // AFTER sign-out.
  let tail: Promise<unknown> = Promise.resolve()
  function serial<T>(op: () => Promise<T>): Promise<T> {
    const run = tail.then(op, op)
    tail = run.catch(() => undefined)
    return run
  }

  return {
    identify: (appUserId) =>
      serial(async () => {
        sdkUsed = true
        const { Purchases } = await import('@revenuecat/purchases-js')
        const purchases = Purchases.isConfigured()
          ? Purchases.getSharedInstance()
          : Purchases.configure({
              apiKey,
              appUserId,
              // The SDK otherwise posts analytics events, carrying the app user
              // id, to e.revenue.cat — which connect-src does not allow, and
              // which nothing here needs. Off, rather than widening the CSP.
              flags: { collectAnalyticsEvents: false },
            })
        const info =
          purchases.getAppUserId() === appUserId
            ? await purchases.getCustomerInfo()
            : await purchases.changeUser(appUserId)
        return Object.values(info.entitlements.active)
          .filter((e) => e.isActive)
          .map((e) => e.identifier)
      }),
    reset: () =>
      serial(async () => {
        if (!sdkUsed) return
        sdkUsed = false
        const { Purchases } = await import('@revenuecat/purchases-js')
        if (Purchases.isConfigured()) Purchases.getSharedInstance().close()
      }),
  }
}
